import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, Bell, Users, Mail, Check, X, Loader2, 
  Settings, Send, AlertCircle, Clock, RefreshCw 
} from 'lucide-react';
import { adminApi } from '../lib/api';

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Notifications & Registrations
  const [notifications, setNotifications] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    email_provider: 'sendgrid',
    sendgrid_api_key: '',
    sender_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.role !== 'super_admin') {
        navigate('/');
        return;
      }
      setUser(parsed);
    } else {
      navigate('/');
      return;
    }
    
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [notifRes, pendingRes, emailRes] = await Promise.all([
        adminApi.getNotifications(),
        adminApi.getPendingRegistrations(),
        adminApi.getEmailSettings()
      ]);
      
      setNotifications(notifRes.data || []);
      setPendingRegistrations(pendingRes.data || []);
      
      if (emailRes.data) {
        setEmailConfigured(emailRes.data.is_configured);
        setEmailSettings(prev => ({
          ...prev,
          email_provider: emailRes.data.email_provider || 'sendgrid',
          sender_email: emailRes.data.sender_email || '',
          smtp_host: emailRes.data.smtp_host || '',
          smtp_port: emailRes.data.smtp_port || 587,
          smtp_user: emailRes.data.smtp_user || '',
          smtp_use_tls: emailRes.data.smtp_use_tls ?? true
        }));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registrationId) => {
    setProcessingId(registrationId);
    try {
      await adminApi.approveRegistration(registrationId, true);
      toast.success('Registrazione approvata! Email inviata all\'utente.');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (registration) => {
    setSelectedRegistration(registration);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRegistration) return;
    
    setProcessingId(selectedRegistration.id);
    try {
      await adminApi.approveRegistration(selectedRegistration.id, false, rejectionReason || null);
      toast.success('Registrazione rifiutata. Email inviata all\'utente.');
      setRejectDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveEmailSettings = async () => {
    if (!emailSettings.sender_email) {
      toast.error('Inserisci l\'email del mittente');
      return;
    }
    
    if (emailSettings.email_provider === 'sendgrid' && !emailSettings.sendgrid_api_key && !emailConfigured) {
      toast.error('Inserisci la API Key di SendGrid');
      return;
    }
    
    setSavingEmail(true);
    try {
      await adminApi.updateEmailSettings(emailSettings);
      toast.success('Impostazioni email salvate!');
      setEmailConfigured(true);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await adminApi.testEmail();
      toast.success('Email di test inviata! Controlla la tua casella.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'email di test');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl">
      <div className="flex items-center gap-3">
        <Shield className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Super Admin</h1>
          <p className="text-muted-foreground mt-1">
            Gestisci registrazioni e configurazioni di sistema
          </p>
        </div>
      </div>

      <Tabs defaultValue="registrations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="registrations" className="gap-2">
            <Users className="w-4 h-4" />
            Registrazioni
            {pendingRegistrations.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingRegistrations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
        </TabsList>

        {/* Pending Registrations Tab */}
        <TabsContent value="registrations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Registrazioni in Attesa
                  </CardTitle>
                  <CardDescription>
                    Approva o rifiuta le richieste di registrazione degli utenti
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Aggiorna
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendingRegistrations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nessuna registrazione in attesa</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRegistrations.map((reg) => (
                    <div 
                      key={reg.id} 
                      className="flex items-center justify-between p-4 border border-border rounded-lg bg-card/50"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{reg.name}</span>
                          <Badge variant="outline">{reg.tenant_id}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{reg.email}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(reg.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleApprove(reg.id)}
                          disabled={processingId === reg.id}
                          className="gap-1"
                        >
                          {processingId === reg.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Approva
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => openRejectDialog(reg)}
                          disabled={processingId === reg.id}
                          className="gap-1"
                        >
                          <X className="w-4 h-4" />
                          Rifiuta
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configurazione Email
              </CardTitle>
              <CardDescription>
                Configura SendGrid o SMTP per inviare email di notifica agli utenti
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Provider Selection */}
              <div className="space-y-2">
                <Label>Provider Email</Label>
                <Select
                  value={emailSettings.email_provider}
                  onValueChange={(value) => setEmailSettings(prev => ({ ...prev, email_provider: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                    <SelectItem value="smtp">SMTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sender Email */}
              <div className="space-y-2">
                <Label>Email Mittente *</Label>
                <Input
                  type="email"
                  placeholder="noreply@tuodominio.com"
                  value={emailSettings.sender_email}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, sender_email: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  L'indirizzo email che apparirà come mittente delle notifiche
                </p>
              </div>

              {/* SendGrid Settings */}
              {emailSettings.email_provider === 'sendgrid' && (
                <div className="space-y-2">
                  <Label>SendGrid API Key {!emailConfigured && '*'}</Label>
                  <Input
                    type="password"
                    placeholder={emailConfigured ? '••••••••••••••••' : 'SG.xxxxxxxxxxxxxxxx'}
                    value={emailSettings.sendgrid_api_key}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Ottieni la API Key da{' '}
                    <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      SendGrid Dashboard
                    </a>
                  </p>
                </div>
              )}

              {/* SMTP Settings */}
              {emailSettings.email_provider === 'smtp' && (
                <div className="space-y-4 pt-2 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host *</Label>
                      <Input
                        placeholder="smtp.gmail.com"
                        value={emailSettings.smtp_host}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Username</Label>
                      <Input
                        placeholder="user@gmail.com"
                        value={emailSettings.smtp_user}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Password</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={emailSettings.smtp_use_tls}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, smtp_use_tls: checked }))}
                    />
                    <Label>Usa TLS</Label>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button onClick={handleSaveEmailSettings} disabled={savingEmail}>
                  {savingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Salva Configurazione
                </Button>
                {emailConfigured && (
                  <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail}>
                    {testingEmail ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invia Email di Test
                  </Button>
                )}
              </div>

              {!emailConfigured && (
                <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-warning mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-warning">Configurazione email mancante</p>
                    <p className="text-muted-foreground">
                      Configura le impostazioni email per poter inviare notifiche di approvazione agli utenti.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rifiuta Registrazione</DialogTitle>
            <DialogDescription>
              {selectedRegistration && (
                <span>
                  Stai per rifiutare la registrazione di <strong>{selectedRegistration.name}</strong> ({selectedRegistration.email})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo del rifiuto (opzionale)</Label>
              <Textarea
                placeholder="Inserisci il motivo del rifiuto..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={processingId}>
              {processingId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Conferma Rifiuto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
